import type { TableSnapshot } from '@tira/protocol';
import { useEffect, useState } from 'react';
import { translate, type Lang } from '../../i18n/index.js';

/**
 * Sitting at a table that has not filled yet.
 *
 * Two screens show this and they must agree, because they are the same wait:
 * the table screen for a public room, and the Kubo screen for a private one —
 * and the Kubo is where a lone player actually sits, since the host stays on
 * that screen until the hand deals.
 *
 * The server arms a bot backfill at every table with a seated human and an
 * empty chair, and publishes when it fires (`TableSnapshot.backfillAt`). All
 * this does is count it down, so the wait has an end on screen rather than an
 * indefinite "hinihintay ang kalaro".
 */

const TICK_MS = 1_000;

export interface SeatWait {
  filled: number;
  empty: number;
  /** What happens if nobody else arrives. */
  line: string;
}

/**
 * The countdown needs a clock of its own. Reading `Date.now()` at render time
 * only re-numbers when something else in the store changes — and nothing does
 * while a table sits empty, so the seconds stood still.
 */
export function useSeatWait(table: TableSnapshot | null, lang: Lang): SeatWait {
  const at = table?.backfillAt;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (at === undefined) return;
    setNow(Date.now());
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= at) clearInterval(id);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [at]);

  const seats = table?.seats ?? [];
  const filled = seats.filter((s) => s !== null).length;
  return { filled, empty: seats.length - filled, line: waitLine(table, now, lang) };
}

export function waitLine(table: TableSnapshot | null, now: number, lang: Lang): string {
  if (table === null) return translate('room.wait.looking', lang);
  if (table.backfillAt === undefined) return translate('room.wait.soon', lang);

  const secs = Math.max(0, Math.round((table.backfillAt - now) / 1000));
  // A Kubo is holding chairs for people who were sent a code; a public table is
  // waiting on strangers. The promise at the end of both is the same one.
  const who = translate(table.kuboCode ? 'room.wait.kubo' : 'room.wait.public', lang);
  return secs === 0
    ? translate('room.wait.arriving', lang, { who })
    : translate('room.wait.countdown', lang, { who, secs });
}
